import { BASE_URL } from "utils/constant";

export function getStatusBadgeClass(status) {
  switch (status) {
    case "Approved":
      return "bg-green-100 text-green-800";
    case "Locked":
      return "bg-blue-100 text-blue-800";
    case "Under Review":
      return "bg-yellow-100 text-yellow-800";
    case "Pending":
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function normalizeFileUrl(maybeUrl) {
  if (!maybeUrl) return null;
  const u = String(maybeUrl);
  if (
    u.startsWith("http://") ||
    u.startsWith("https://") ||
    u.startsWith("data:") ||
    u.startsWith("blob:")
  ) {
    return u;
  }
  // Many backends return a relative media path like "/media/..." – prefix API base.
  return `${BASE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
}

function normalizeDesignStatus(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "Pending";
  if (s === "approved") return "Approved";
  if (s === "locked") return "Locked";
  if (s === "under review" || s === "under_review" || s === "under-review") return "Under Review";
  // backend sometimes uses draft/pending for not-yet-approved
  if (s === "draft" || s === "pending") return "Draft";
  // fallback: Title Case-ish
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export async function downloadDiagram(url, suggestedName = "design") {
  if (!url) {
    alert("No file available to download");
    return;
  }

  try {
    // data: or blob: urls can be downloaded directly
    if (url.startsWith("data:") || url.startsWith("blob:")) {
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestedName.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    const accessToken = localStorage.getItem("access");
    // If the URL already contains a SAS token (common with Azure blobs: sig=, sv=, se=),
    // do NOT include an Authorization header — it will cause authentication failures (403).
    const looksLikeSas = url.includes("sig=") || url.includes("sv=") || url.includes("se=") || url.includes("skoid=") || url.includes("sktid=") || url.includes("skt=");
    const headers = {};
    if (!looksLikeSas && accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const res = await fetch(url, { headers });

    if (!res.ok) throw new Error("Network response was not ok");
    const blob = await res.blob();

    // Determine filename
    let filename = suggestedName.replace(/\s+/g, "_");
    const contentDisposition = res.headers.get("content-disposition") || "";
    const contentType = res.headers.get("content-type") || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    } else {
      const ext = (contentType.split("/").pop() || "").split(";")[0];
      if (ext) filename = `${filename}.${ext}`;
      else {
        const parts = url.split("/");
        const last = parts[parts.length - 1] || "";
        filename = decodeURIComponent(last.split("?")[0] || filename);
      }
    }

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed", err);
    alert("Download failed");
  }
}

export async function fetchCommentsForDesign(designId) {
  if (!designId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/design/designs/${designId}/comments/`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || err?.message || "Failed to fetch comments");
    }
    const json = await res.json();
    // API may return array directly or { results: [...] }
    return Array.isArray(json) ? json : (json?.results || json?.data || []);
  } catch (err) {
    console.error("fetchCommentsForDesign failed", err);
    return [];
  }
}

export async function createCommentForDesign(designId, commentText) {
  if (!designId || !commentText) throw new Error("Invalid input");
  const accessToken = localStorage.getItem("access");
  const body = { comment: commentText, design: designId };
  const res = await fetch(`${BASE_URL}/design/designs/comments/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to create comment");
  }
  const json = await res.json();
  return json;
}

export async function fetchPinsForDesign(designId) {
  if (!designId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/design/designs/${designId}/pins/`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.detail || err?.message || "Failed to fetch pins");
    }
    const json = await res.json().catch(() => ({}));
    return Array.isArray(json) ? json : (json?.results || json?.data || []);
  } catch (err) {
    console.error("fetchPinsForDesign failed", err);
    return [];
  }
}

export async function createPinForDesign(designId, pin) {
  if (!designId) throw new Error("Invalid design id");
  if (!pin) throw new Error("Invalid pin input");
  const accessToken = localStorage.getItem("access");

  const body = {
    design: designId,
    x_coordinate: pin.x_coordinate,
    y_coordinate: pin.y_coordinate,
    comment: pin.comment,
    ...(typeof pin.assigned_to !== "undefined" && pin.assigned_to !== null
      ? { assigned_to: pin.assigned_to }
      : {}),
  };

  const res = await fetch(`${BASE_URL}/design/designs/pins/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to create pin");
  }
  return json;
}

export async function updatePinById(pinId, payload) {
  if (!pinId) throw new Error("Invalid pin id");
  const accessToken = localStorage.getItem("access");

  const res = await fetch(`${BASE_URL}/design/designs/pins/${pinId}/update//`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to update pin");
  }
  return json;
}

export async function deleteCommentById(commentId) {
  if (!commentId) throw new Error("Invalid comment id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/comments/${commentId}/delete/`, {
    method: "DELETE",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to delete comment");
  }
  return true;
}

export async function getDesignsByOrg(orgId) {
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/org/${orgId}/`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const json = await res.json();
  const list = Array.isArray(json) ? json : (json?.data || json?.results || []);
  return list.map(item => ({
    id: item.id || item.pk || String(item.id || Date.now()),
    name: item.design_name || item.name || "Untitled Design",
    projectName: item.site?.site_name || item.site_name || String(item.site || "Unknown Project"),
    status: normalizeDesignStatus(item.status),
    lastUpdated: item.last_updated_on?.split("T")[0] || item.lastUpdated || new Date().toISOString().split("T")[0],
    approverName: item.approver_name || item.approverName || "Pending",
    fileType: item.type || item.fileType || "Layout",
    uploadedBy: item.created_by || "Unknown",
    diagram: normalizeFileUrl(item.diagram),
    assignee: (() => {
      const raw = item.assign_to;
      if (Array.isArray(raw) && raw.length > 0) {
        // Sometimes backend returns names; sometimes ids; sometimes member objects.
        if (raw.every((x) => typeof x === "string")) return raw.join(", ");
        if (raw.every((x) => x && typeof x === "object")) {
          return raw
            .map((m) => {
              const fn = m?.user?.first_name || "";
              const ln = m?.user?.last_name || "";
              const full = `${fn} ${ln}`.trim();
              return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
            })
            .join(", ");
        }
        // numeric ids -> don't show ids in UI list
        return "Assigned";
      }
      return item.assignee || "Unassigned";
    })(),
    linkedTask: undefined,
    linkedTasks: [],
    versions: item.versions || [],
    pins: item.pins || [],
    comments: item.comments || [],
  }));
}

export async function replaceDesignFile(designId, file) {
  if (!designId) throw new Error("Invalid design id");
  if (!file) throw new Error("No file selected");
  const accessToken = localStorage.getItem("access");
  const formPayload = new FormData();
  // backend expects `diagram` (same as create)
  formPayload.append("diagram", file);

  const res = await fetch(`${BASE_URL}/design/designs/${designId}/replace/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: formPayload,
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to replace file");
  }
  return json;
}

export async function approveDesignById(designId) {
  if (!designId) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/approve/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to approve design");
  }
  return json;
}

export async function lockDesignById(designId) {
  if (!designId) throw new Error("Invalid design id");
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/lock/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to lock design");
  }
  return json;
}

export async function fetchDesignVersions(designId) {
  if (!designId) return [];
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/versions/`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to fetch version history");
  }
  const list = Array.isArray(json) ? json : (json?.results || json?.data || []);

  // Normalize into a UI-friendly shape (but keep unknown fields for flexibility)
  return (list || []).map((v, idx) => {
    const versionLabel =
      v.version ||
      v.version_name ||
      v.label ||
      (typeof v.version_number !== "undefined" ? `V${v.version_number}` : undefined) ||
      `V${idx + 1}`;

    const uploadedBy =
      (v.uploaded_by &&
        (typeof v.uploaded_by === "string"
          ? v.uploaded_by
          : v.uploaded_by.username || v.uploaded_by.name)) ||
      v.uploadedBy ||
      v.created_by ||
      v.createdBy ||
      "Unknown";

    const uploadDateRaw = v.upload_date || v.created_at || v.createdAt || v.uploaded_on || v.uploadedOn;
    const uploadDate = uploadDateRaw ? String(uploadDateRaw).split("T")[0] : "";

    const status = normalizeDesignStatus(v.status);

    const fileUrlRaw =
      v.diagram ||
      v.file ||
      v.url ||
      v.file_url ||
      v.fileUrl ||
      v.download_url ||
      v.downloadUrl ||
      null;
    const fileUrl = normalizeFileUrl(fileUrlRaw);

    return {
      ...v,
      id: v.id || v.pk || `${designId}-v-${idx}`,
      version: versionLabel,
      name: v.name || v.design_name || v.title || v.file_name || v.filename || "Design File",
      uploadedBy,
      uploadDate,
      status,
      fileUrl,
    };
  });
}

export async function deleteDesignById(designId) {
  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/${designId}/delete/`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || err?.message || "Failed to delete design");
  }
  return true;
}

export async function fetchDesignMembers(designId) {
  if (!designId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/design/designs/members/${designId}/`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.detail || json?.message || "Failed to fetch design members");
    }
    return Array.isArray(json) ? json : (json?.results || json?.data || []);
  } catch (err) {
    console.error("fetchDesignMembers failed", err);
    return [];
  }
}

export async function addMemberToDesign(designId, memberId) {
  if (!designId) throw new Error("Invalid design id");
  if (!memberId && memberId !== 0) throw new Error("Invalid member id");

  const accessToken = localStorage.getItem("access");
  const res = await fetch(`${BASE_URL}/design/designs/members/${designId}/create/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ member_id: Number(memberId) }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.detail || json?.message || "Failed to add design member");
  }
  return json;
}

export async function fetchOrganizationMembers(organizationId) {
  if (!organizationId) return [];
  try {
    const accessToken = localStorage.getItem("access");
    const res = await fetch(`${BASE_URL}/org/organization/${organizationId}/members/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.detail || json?.message || "Failed to fetch organization members");
    }
    // org endpoints in this repo commonly return { status, data: [...] }
    const list = Array.isArray(json) ? json : (json?.data || json?.results || []);
    return list;
  } catch (err) {
    console.error("fetchOrganizationMembers failed", err);
    return [];
  }
}